/**NIRSALReports — 5 institutional PDF reports */
import React, { useState } from "react";
import { useNIRSAL } from "@/context/NIRSALContext";
import { PageHeader } from "@/components/ds";
import { toast } from "sonner";
import { FileText, Download, Loader } from "lucide-react";

const REPORTS = [
  { key:"portfolio", label:"Portfolio Climate & Carbon Asset Report", badge:"Primary", badgeClass:"bg-emerald-100 text-emerald-700",
    desc:"Comprehensive portfolio-level report: KPI dashboard, CPA rankings, pathways, SSOT validation, QR-verified. Suitable for investor and regulator briefing.",
    endpoint:"portfolio", filename:"NIRSAL-Portfolio-Report.pdf" },
  { key:"green-credit", label:"Green Credit & Guarantee Portfolio Report", badge:"Finance", badgeClass:"bg-blue-100 text-blue-700",
    desc:"CRG-focused report: capital mobilized, guarantee coverage, leverage ratios, financing product breakdown, PFI pipeline.",
    endpoint:"green-credit", filename:"NIRSAL-GreenCredit-Report.pdf" },
  { key:"carbon-structuring", label:"Carbon Structuring & PoA Report", badge:"Structuring", badgeClass:"bg-purple-100 text-purple-700",
    desc:"Art.6.4 + VCM + plastic structuring: CPA groupings, PoA architecture, additionality signals, DMRV readiness, NCCC pathway.",
    endpoint:"carbon-structuring", filename:"NIRSAL-CarbonStructuring-Report.pdf" },
  { key:"sdg-eesg", label:"SDG & EESG Impact Report", badge:"Impact", badgeClass:"bg-amber-100 text-amber-700",
    desc:"7 SDGs addressed. EESG framework (Environmental · Economic · Social · Governance). OECD Rio Marker 2 climate finance alignment.",
    endpoint:"sdg-eesg", filename:"NIRSAL-SDG-EESG-Report.pdf" },
  { key:"art64-readiness", label:"Article 6.4 Program Readiness Report", badge:"Art.6.4", badgeClass:"bg-[#1e3a5f]/10 text-[#1e3a5f]",
    desc:"PACM / Article 6.4 readiness: host country alignment, NCCC engagement status, corresponding adjustment requirements, eligible clusters, DMRV coverage.",
    endpoint:"art64-readiness", filename:"NIRSAL-Art64-Readiness-Report.pdf" },
];

export default function NIRSALReports() {
  const { selectedPeriod, periods, setSelectedPeriod, consolidated } = useNIRSAL();
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (key, endpoint, filename) => {
    if (!selectedPeriod) return;
    setDownloading(key);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nirsal/pdf/${endpoint}/${selectedPeriod}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("decarboniq_token")}` } }
      );
      if (!r.ok) throw new Error(r.statusText);
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = filename.replace(".pdf", `-${selectedPeriod}.pdf`);
      a.click();
      URL.revokeObjectURL(url);
      toast.success(filename + " downloaded");
    } catch {
      toast.error("Report generation failed");
    } finally {
      setDownloading(null);
    }
  };

  const p = consolidated?.portfolio || {};

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <PageHeader label="REPORTS" title="" />
          <p className="text-xs text-slate-400 -mt-4">5 institutional-grade reports · Dynamic · QR-verifiable · Production-ready</p>
        </div>
        <select value={selectedPeriod || ""} onChange={e => setSelectedPeriod(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
          {periods.map(pp => <option key={pp.period_id} value={pp.period_id}>{pp.label}</option>)}
        </select>
      </div>

      {/* Verification badge */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-slate-600">Portfolio State</p>
          <p className="text-xs text-slate-400 mt-0.5">{p.total_clusters || 0} clusters · {(p.total_reduction_tco2e_yr||0).toLocaleString()} tCO₂e/yr · Readiness {consolidated?.readiness_score || "—"}/100</p>
        </div>
        <span className="text-xs font-bold px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-100 rounded-xl">Validation-Ready Model Output</span>
      </div>

      {/* Report cards */}
      <div className="space-y-4">
        {REPORTS.map(r => (
          <div key={r.key} className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 flex items-start justify-between gap-4" data-testid={`report-card-${r.key}`}>
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText size={18} className="text-slate-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-sm font-bold text-slate-800">{r.label}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${r.badgeClass}`}>{r.badge}</span>
                </div>
                <p className="text-xs text-slate-500">{r.desc}</p>
              </div>
            </div>
            <button
              onClick={() => handleDownload(r.key, r.endpoint, r.filename)}
              disabled={downloading === r.key || !selectedPeriod}
              className="flex items-center gap-2 bg-[#1e3a5f] text-white text-xs px-4 py-2 rounded-lg hover:bg-blue-900 transition-all disabled:opacity-60 flex-shrink-0"
              data-testid={`download-${r.key}`}
            >
              {downloading === r.key ? <Loader size={12} className="animate-spin" /> : <Download size={12} />}
              {downloading === r.key ? "Generating…" : "Download PDF"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
